<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ServerStatsStandalone                                 |
| Section: views/overviews                                         |
| Purpose: Standalone operational dashboard for backend metrics.   |
+------------------------------------------------------------------+
-->
<template>
    <div class="server-stats-page">
        <div class="server-stats-shell">
            <header class="server-stats-hero">
                <p class="eyebrow">{{ $t('serverStatsStandalone.source') }}</p>
                <h1>{{ $t('serverStatsStandalone.title') }}</h1>
                <p class="subtitle">{{ $t('serverStatsStandalone.subtitle') }}</p>
                <div class="meta-strip">
                    <span class="meta-pill">{{ $t('serverStatsStandalone.signedInAs', { username: usernameLabel })
                        }}</span>
                    <span class="meta-pill">{{ $t('serverStatsStandalone.generatedAt') }}: {{ generatedAtLabel }}</span>
                    <span class="meta-pill">{{ $t('serverStatsStandalone.mode') }}: {{ modeLabel }}</span>
                </div>
            </header>

            <section v-if="loading" class="state-panel">
                <p>{{ $t('serverStatsStandalone.loading') }}</p>
            </section>

            <section v-else-if="forbidden" class="state-panel state-panel--error">
                <p>{{ $t('serverStatsStandalone.forbidden') }}</p>
            </section>

            <section v-else-if="errorMessage" class="state-panel state-panel--error">
                <p>{{ errorMessage }}</p>
            </section>

            <template v-else-if="stats">
                <section class="status-banner" :class="bannerClass">
                    <strong>{{ $t('serverStatsStandalone.source') }}:</strong>
                    <span>{{ providerStatusText }}</span>
                </section>

                <section class="stats-section">
                    <div class="section-heading">
                        <h2>{{ $t('serverStatsStandalone.hostSection') }}</h2>
                        <p>{{ hostLabel }}</p>
                    </div>

                    <div class="card-grid">
                        <article class="stat-card stat-card--highlight">
                            <span class="stat-label">{{ $t('serverStatsStandalone.hostUptime') }}</span>
                            <strong class="stat-value">{{ formatDuration(stats.host?.uptimeSeconds) }}</strong>
                            <span class="stat-detail">{{ $t('serverStatsStandalone.hostname') }}: {{
                                stats.host?.hostname || unavailableLabel }}</span>
                        </article>

                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.loadAverage') }}</span>
                            <strong class="stat-value">{{ formatLoadAverage(stats.host?.loadAverage) }}</strong>
                            <span class="stat-detail">{{ $t('serverStatsStandalone.platform') }}: {{ platformLabel
                                }}</span>
                        </article>

                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.cpu') }}</span>
                            <strong class="stat-value">{{ cpuLabel }}</strong>
                            <span class="stat-detail">{{ cpuDetailLabel }}</span>
                        </article>

                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.memory') }}</span>
                            <strong class="stat-value">{{ hostMemoryPercentLabel }}</strong>
                            <span class="stat-detail">{{ hostMemoryDetailLabel }}</span>
                        </article>

                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.disk') }}</span>
                            <strong class="stat-value">{{ diskPercentLabel }}</strong>
                            <span class="stat-detail">{{ diskDetailLabel }}</span>
                        </article>
                    </div>
                </section>

                <section class="stats-section">
                    <div class="section-heading">
                        <h2>{{ $t('serverStatsStandalone.appSection') }}</h2>
                        <p>{{ appModeLabel }}</p>
                    </div>

                    <div class="card-grid card-grid--compact">
                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.appUptime') }}</span>
                            <strong class="stat-value">{{ formatDuration(stats.app?.uptimeSeconds) }}</strong>
                            <span class="stat-detail">Node.js</span>
                        </article>

                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.appMemory') }}</span>
                            <strong class="stat-value">{{ appMemoryLabel }}</strong>
                            <span class="stat-detail">RSS: {{ formatBytes(stats.app?.memory?.rssBytes) }}</span>
                        </article>

                        <article class="stat-card">
                            <span class="stat-label">{{ $t('serverStatsStandalone.cache') }}</span>
                            <strong class="stat-value">{{ cacheKeysLabel }}</strong>
                            <span class="stat-detail">Hits: {{ formatInteger(stats.app?.cache?.hits) }} · Misses: {{
                                formatInteger(stats.app?.cache?.misses) }}</span>
                        </article>
                    </div>
                </section>
            </template>
        </div>
    </div>
</template>

<script>
import { fetchServerStats } from '@/api/apiService';
import { useAuth } from '@/composables/useAuth';

export default {
    name: 'ServerStatsStandalone',
    setup() {
        const { authToken, username } = useAuth();

        return {
            authToken,
            username,
        };
    },
    data() {
        return {
            loading: true,
            forbidden: false,
            errorMessage: '',
            stats: null,
        };
    },
    computed: {
        resolvedAuthToken() {
            const injectedToken = typeof this.authToken === 'string' ? this.authToken : this.authToken?.value;
            return injectedToken || localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || '';
        },
        usernameLabel() {
            const injectedUsername = typeof this.username === 'string' ? this.username : this.username?.value;
            return injectedUsername || localStorage.getItem('username') || sessionStorage.getItem('username') || 'unknown';
        },
        generatedAtLabel() {
            return this.stats?.generatedAt ? this.formatDateTime(this.stats.generatedAt) : this.unavailableLabel;
        },
        modeLabel() {
            return this.stats?.mode === 'provider'
                ? this.$t('serverStatsStandalone.provider')
                : this.$t('serverStatsStandalone.fallback');
        },
        providerStatusText() {
            if (this.stats?.mode === 'provider') {
                return this.$t('serverStatsStandalone.provider');
            }

            if (this.stats?.providerConfigured) {
                return this.$t('serverStatsStandalone.providerUnavailable');
            }

            return this.$t('serverStatsStandalone.providerNotConfigured');
        },
        bannerClass() {
            return this.stats?.mode === 'provider' ? 'status-banner--ok' : 'status-banner--warn';
        },
        hostLabel() {
            return this.stats?.host?.source || this.unavailableLabel;
        },
        platformLabel() {
            const platform = this.stats?.host?.platform;
            const release = this.stats?.host?.release;
            return [platform, release].filter(Boolean).join(' ') || this.unavailableLabel;
        },
        cpuLabel() {
            return this.$t('serverStatsStandalone.cpuCores', {
                count: this.formatInteger(this.stats?.host?.cpu?.cores),
            });
        },
        cpuDetailLabel() {
            const model = this.stats?.host?.cpu?.model;
            const speed = this.stats?.host?.cpu?.speedMHz;
            if (!model && !speed) {
                return this.unavailableLabel;
            }
            return [model, speed ? `${speed} MHz` : null].filter(Boolean).join(' · ');
        },
        hostMemoryPercentLabel() {
            const usagePercent = this.stats?.host?.memory?.usagePercent;
            return usagePercent === null || usagePercent === undefined
                ? this.unavailableLabel
                : `${this.formatDecimal(usagePercent)}%`;
        },
        hostMemoryDetailLabel() {
            const used = this.formatBytes(this.stats?.host?.memory?.usedBytes);
            const total = this.formatBytes(this.stats?.host?.memory?.totalBytes);
            return this.$t('serverStatsStandalone.bytesUsedOfTotal', { used, total });
        },
        diskPercentLabel() {
            const usagePercent = this.stats?.host?.disk?.usagePercent;
            return usagePercent === null || usagePercent === undefined
                ? this.unavailableLabel
                : `${this.formatDecimal(usagePercent)}%`;
        },
        diskDetailLabel() {
            const disk = this.stats?.host?.disk;
            if (!disk) {
                return this.unavailableLabel;
            }

            const used = this.formatBytes(disk.usedBytes);
            const total = this.formatBytes(disk.totalBytes);
            const mount = disk.mount || '/';
            return `${mount} · ${this.$t('serverStatsStandalone.bytesUsedOfTotal', { used, total })}`;
        },
        appModeLabel() {
            return this.stats?.mode === 'provider'
                ? this.$t('serverStatsStandalone.providerConfigured')
                : this.$t('serverStatsStandalone.fallback');
        },
        appMemoryLabel() {
            return this.formatBytes(this.stats?.app?.memory?.heapUsedBytes);
        },
        cacheKeysLabel() {
            return this.formatInteger(this.stats?.app?.cache?.keys);
        },
        unavailableLabel() {
            return this.$t('serverStatsStandalone.unavailable');
        },
    },
    async mounted() {
        if (!this.resolvedAuthToken) {
            this.loading = false;
            this.errorMessage = this.$t('serverStatsStandalone.unauthorized');
            return;
        }

        await this.loadStats();
    },
    methods: {
        async loadStats() {
            this.loading = true;
            this.forbidden = false;
            this.errorMessage = '';

            try {
                this.stats = await fetchServerStats();
            } catch (error) {
                const status = error.response?.status;

                if (status === 401) {
                    this.$router.replace({
                        path: this.$route.params.locale ? `/${this.$route.params.locale}/login` : '/login',
                        query: { redirect: this.$route.fullPath },
                    });
                    return;
                }

                if (status === 403) {
                    this.forbidden = true;
                    return;
                }

                this.errorMessage = this.$t('serverStatsStandalone.loadError');
            } finally {
                this.loading = false;
            }
        },
        formatInteger(value) {
            if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return this.unavailableLabel;
            }
            return new Intl.NumberFormat(this.$i18n.locale).format(Number(value));
        },
        formatDecimal(value) {
            if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return this.unavailableLabel;
            }
            return new Intl.NumberFormat(this.$i18n.locale, {
                maximumFractionDigits: 2,
            }).format(Number(value));
        },
        formatBytes(value) {
            if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return this.unavailableLabel;
            }

            const numericValue = Number(value);
            if (numericValue < 1024) {
                return `${this.formatDecimal(numericValue)} B`;
            }

            const units = ['KB', 'MB', 'GB', 'TB'];
            let unitIndex = -1;
            let scaledValue = numericValue;

            do {
                scaledValue /= 1024;
                unitIndex += 1;
            } while (scaledValue >= 1024 && unitIndex < units.length - 1);

            return `${this.formatDecimal(scaledValue)} ${units[unitIndex]}`;
        },
        formatDuration(value) {
            if (value === null || value === undefined || Number.isNaN(Number(value))) {
                return this.unavailableLabel;
            }

            const totalSeconds = Math.max(Math.floor(Number(value)), 0);
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);

            const parts = [];
            if (days) parts.push(`${days}d`);
            if (hours || days) parts.push(`${hours}h`);
            parts.push(`${minutes}m`);
            return parts.join(' ');
        },
        formatLoadAverage(loadAverage) {
            if (!Array.isArray(loadAverage) || !loadAverage.length) {
                return this.unavailableLabel;
            }

            return loadAverage
                .slice(0, 3)
                .map(value => this.formatDecimal(value))
                .join(' / ');
        },
        formatDateTime(value) {
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) {
                return this.unavailableLabel;
            }

            return new Intl.DateTimeFormat(this.$i18n.locale, {
                dateStyle: 'medium',
                timeStyle: 'short',
            }).format(date);
        },
    },
};
</script>

<style scoped>
.server-stats-page {
    min-height: 100vh;
    padding: 32px;
    background:
        radial-gradient(circle at top left, rgba(204, 160, 93, 0.28), transparent 32%),
        linear-gradient(145deg, #101724 0%, #162334 52%, #203047 100%);
    color: #f7f1e6;
}

.server-stats-shell {
    width: min(1120px, 100%);
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.server-stats-hero {
    padding: 28px;
    border: 1px solid rgba(247, 241, 230, 0.12);
    background: rgba(8, 14, 24, 0.42);
    backdrop-filter: blur(16px);
    box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
}

.eyebrow {
    margin: 0 0 8px;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 0.78rem;
    color: #d9b97d;
}

h1,
h2,
.stat-value {
    font-family: Georgia, 'Times New Roman', serif;
}

h1 {
    margin: 0;
    font-size: clamp(2.4rem, 4vw, 4.2rem);
    line-height: 0.95;
}

.subtitle,
.section-heading p,
.stat-detail,
.state-panel p,
.status-banner {
    color: rgba(247, 241, 230, 0.78);
}

.subtitle {
    margin: 12px 0 0;
    max-width: 44rem;
    font-size: 1rem;
    line-height: 1.6;
}

.meta-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
}

.meta-pill {
    padding: 10px 14px;
    border: 1px solid rgba(247, 241, 230, 0.1);
    background: rgba(247, 241, 230, 0.06);
    font-size: 0.92rem;
}

.status-banner,
.state-panel,
.stats-section {
    padding: 24px 28px;
    border: 1px solid rgba(247, 241, 230, 0.1);
    background: rgba(8, 14, 24, 0.58);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
}

.status-banner {
    display: flex;
    gap: 12px;
    align-items: center;
}

.status-banner--ok {
    border-color: rgba(124, 199, 156, 0.4);
}

.status-banner--warn,
.state-panel--error {
    border-color: rgba(217, 185, 125, 0.35);
}

.section-heading {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: end;
    margin-bottom: 18px;
}

.section-heading h2 {
    margin: 0;
    font-size: 1.6rem;
}

.section-heading p,
.stat-label,
.stat-detail,
.meta-pill,
.status-banner,
.state-panel p {
    margin: 0;
}

.card-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
}

.card-grid--compact {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.stat-card {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 18px;
    min-height: 170px;
    border: 1px solid rgba(247, 241, 230, 0.08);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
}

.stat-card--highlight {
    background: linear-gradient(180deg, rgba(217, 185, 125, 0.16), rgba(255, 255, 255, 0.03));
}

.stat-label {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.78rem;
    color: rgba(247, 241, 230, 0.62);
}

.stat-value {
    font-size: clamp(1.8rem, 2.5vw, 2.6rem);
    line-height: 1;
}

.stat-detail {
    margin-top: auto;
    font-size: 0.92rem;
    line-height: 1.5;
}

@media (max-width: 720px) {
    .server-stats-page {
        padding: 16px;
    }

    .server-stats-hero,
    .status-banner,
    .state-panel,
    .stats-section {
        padding: 20px;
    }

    .section-heading {
        flex-direction: column;
        align-items: flex-start;
    }

    .status-banner {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
